import { useState, useEffect, useCallback } from 'react';
import { Repeat, ChevronDown, ChevronUp, Copy, ExternalLink, Loader2 } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';
import { apiErrorText } from '../../lib/apiError';

const STATUS_DOT = {
  active: 'bg-blue-500',
  pending: 'bg-blue-300',
  past_due: 'bg-red-500',
  paused: 'bg-gray-400',
  cancelled: 'bg-gray-300',
};

const STATUS_LABEL = {
  active: 'Active',
  pending: 'Awaiting first payment',
  past_due: 'Past due',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

const money = (n, c) => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Operator view of recurring service subscriptions — Stripe-native and the
 *  manual (Ziina / no-processor) recurring-invoice engine. Flat financial table. */
export const SubscriptionsPanel = ({ orgId, hideWhenEmpty = false }) => {
  const [subs, setSubs] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) { setSubs([]); return; }
    try {
      const r = await apiV2.get(`/organizations/${orgId}/subscriptions`);
      setSubs(r.data?.subscriptions || []);
    } catch {
      setSubs([]);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const act = async (sub, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(`${sub.id}:${action}`);
    try {
      await apiV2.post(`/organizations/${orgId}/subscriptions/${sub.id}/${action}`);
      toast.success({
        pause: 'Subscription paused', resume: 'Subscription resumed',
        cancel: 'Subscription cancelled', 'mark-paid': 'Cycle marked paid — receipt sent',
      }[action] || 'Done');
      await load();
    } catch (e) {
      toast.error(apiErrorText(e, 'Action failed'));
    } finally {
      setBusy(null);
    }
  };

  const copyManageLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Manage link copied');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? toast.success('Manage link copied') : toast.error('Could not copy — long-press the link instead');
    }
  };

  if (subs === null) return null;
  if (hideWhenEmpty && subs.length === 0) return null;

  const actionBtn = 'h-8 px-3 rounded-lg text-[12px] text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50';

  return (
    <div className="card-soft p-5" data-testid="subscriptions-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 text-gray-400" />
          <p className="text-sm text-gray-900">Subscriptions</p>
        </div>
        <span className="text-xs text-gray-400">{subs.length} subscriber{subs.length === 1 ? '' : 's'}</span>
      </div>

      {subs.length === 0 ? (
        <p className="text-xs text-gray-400 mt-3" data-testid="subscriptions-empty">
          No subscribers yet. Create a service with the <span className="text-gray-600">Subscription</span> pricing model and share your booking page.
        </p>
      ) : (
        <div className="mt-3 divide-y divide-gray-100">
          {subs.map((s) => {
            const expanded = open === s.id;
            return (
              <div key={s.id} data-testid={`subscription-row-${s.id}`}>
                <button
                  type="button"
                  data-testid={`subscription-row-toggle-${s.id}`}
                  onClick={() => setOpen(expanded ? null : s.id)}
                  className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 rounded-lg px-1.5 -mx-1.5 transition-colors"
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[s.status] || 'bg-gray-300'}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] text-gray-900 truncate">{s.customerName || s.customerEmail || 'Subscriber'}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{s.serviceName}</span>
                  </span>
                  <span className="text-right flex-shrink-0">
                    <span className="block text-[13px] text-gray-900 tabular-nums">{money(s.amount, s.currency)} <span className="text-gray-400">/ {s.interval}</span></span>
                    <span className="block text-[11px] text-gray-400">
                      {STATUS_LABEL[s.status] || s.status}
                      {s.cancelRequested && s.status !== 'cancelled' ? ' · cancel requested' : ''}
                    </span>
                  </span>
                  {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-300" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-300" />}
                </button>

                {expanded && (
                  <div className="pb-3 pl-5 pr-1.5" data-testid={`subscription-detail-${s.id}`}>
                    <div className="bg-gray-50 rounded-xl p-3 text-[12px] text-gray-600 space-y-1">
                      {s.cancelRequested && s.status !== 'cancelled' && (
                        <p className="text-red-600 font-medium" data-testid="subscription-cancel-requested-note">Customer requested cancellation — confirm below.</p>
                      )}
                      <p><span className="text-gray-400">Email:</span> {s.customerEmail || '—'}{s.customerPhone ? ` · ${s.customerPhone}` : ''}</p>
                      <p><span className="text-gray-400">Billing:</span> {s.intervalLabel} · {s.provider === 'stripe' ? 'Stripe (automatic)' : 'Invoice engine (link + reminders)'} · {s.cycleCount} cycle{s.cycleCount === 1 ? '' : 's'} billed</p>
                      <p><span className="text-gray-400">Next billing:</span> {s.nextBillingDate || '—'}</p>
                      {s.latestInvoice && (
                        <p>
                          <span className="text-gray-400">Latest invoice:</span> {s.latestInvoice.invoiceNumber} · {s.latestInvoice.status}
                          <a href={s.latestInvoice.pageUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline ml-1.5 inline-flex items-center gap-0.5">
                            view <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {s.provider === 'manual' && s.latestInvoice?.status === 'pending' && s.status !== 'cancelled' && (
                        <button data-testid={`subscription-mark-paid-${s.id}`} disabled={!!busy} onClick={() => act(s, 'mark-paid')} className={`${actionBtn} !bg-blue-50 !text-blue-700 hover:!bg-blue-100`}>
                          {busy === `${s.id}:mark-paid` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Mark cycle paid'}
                        </button>
                      )}
                      {(s.status === 'active' || s.status === 'past_due') && (
                        <button data-testid={`subscription-pause-${s.id}`} disabled={!!busy} onClick={() => act(s, 'pause')} className={actionBtn}>Pause</button>
                      )}
                      {(s.status === 'paused' || s.status === 'past_due') && (
                        <button data-testid={`subscription-resume-${s.id}`} disabled={!!busy} onClick={() => act(s, 'resume')} className={actionBtn}>Resume</button>
                      )}
                      {s.status !== 'cancelled' && (
                        <button
                          data-testid={`subscription-cancel-${s.id}`}
                          disabled={!!busy}
                          onClick={() => act(s, 'cancel', `Cancel ${s.customerName || s.customerEmail}'s ${s.intervalLabel.toLowerCase()} subscription? This stops all future billing.`)}
                          className={`${actionBtn} !bg-red-50 !text-red-600 hover:!bg-red-100`}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        data-testid={`subscription-copy-link-${s.id}`}
                        onClick={() => copyManageLink(s.manageUrl)}
                        className={`${actionBtn} inline-flex items-center gap-1.5`}
                      >
                        <Copy className="w-3 h-3" /> Manage link
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubscriptionsPanel;
